import React, { useCallback, useState } from 'react';

import { apiReportJourneyAbuse } from 'src/utils/journeyApi';

type Props = {
  aliasUUID: string;
};

/**
 * "Report this page" for a Journey a viewer is looking at (JOU-42).
 *
 * The iframe sandbox stops a hosted HTML artifact attacking Journey. It does
 * nothing about someone uploading a convincing fake page and sharing it on a
 * jny.io link, where the domain lends the credibility. That risk is closed by
 * letting the person who sees it tell us, not by isolation.
 *
 * Deliberately understated. This sits beside the Journey badge and should be
 * invisible to the 99.9% of viewers looking at a legitimate Journey, while
 * being findable by someone who has just been asked for their bank password.
 */
export const ReportAbuseLink = ({ aliasUUID }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  const onSubmit = useCallback(async () => {
    if (!reason.trim()) {
      setError('Please describe the problem.');
      return;
    }
    setState('sending');
    setError('');
    try {
      await apiReportJourneyAbuse(aliasUUID, {
        reason,
        email: email || undefined,
        url: window.location.href,
      });
      setState('sent');
    } catch (e: any) {
      // A rejected report is worth an explicit message: someone reporting a
      // phishing page has every reason to assume the silence is the site
      // protecting itself.
      setState('error');
      setError(e?.response?.data?.message || 'That could not be sent. Please email support@journey.io.');
    }
  }, [aliasUUID, reason, email]);

  if (!isOpen) {
    return (
      <button
        type='button'
        onClick={() => setIsOpen(true)}
        className='text-bedrock-serif-small text-bedrock-dark-gray hover:text-bedrock-black transition-colors'
      >
        Report this page
      </button>
    );
  }

  return (
    <div className='fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4'>
      <div className='w-full max-w-md rounded-xl bg-white p-6 shadow-xl'>
        {state === 'sent' ? (
          <>
            <h2 className='text-bedrock-h3 mb-2'>Thank you</h2>
            <p className='text-bedrock-p text-bedrock-dark-gray mb-5'>
              We have received your report and will review it.
            </p>
            <button type='button' className='btn-bedrock-primary w-full' onClick={() => setIsOpen(false)}>
              Close
            </button>
          </>
        ) : (
          <>
            <h2 className='text-bedrock-h3 mb-2'>Report this page</h2>
            <p className='text-bedrock-p text-bedrock-dark-gray mb-4'>
              Tell us what is wrong with this page. If it is asking for passwords or payment details, say so
              and do not enter anything.
            </p>

            <label className='text-bedrock-p-strong block mb-1'>What is wrong?</label>
            <textarea
              className='input-bedrock w-full h-28 mb-4'
              placeholder='For example: this page is pretending to be a bank login'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={state === 'sending'}
            />

            <label className='text-bedrock-p-strong block mb-1'>Your email (optional)</label>
            <input
              type='email'
              className='input-bedrock w-full mb-4'
              placeholder='So we can follow up'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={state === 'sending'}
            />

            {error && <p className='text-bedrock-p text-bedrock-red mb-3'>{error}</p>}

            <div className='flex gap-2'>
              <button
                type='button'
                className='btn-bedrock-secondary flex-1'
                onClick={() => setIsOpen(false)}
                disabled={state === 'sending'}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn-bedrock-primary flex-1'
                onClick={onSubmit}
                disabled={state === 'sending' || !reason.trim()}
              >
                {state === 'sending' ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportAbuseLink;
